import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Activity, BookOpen, Coffee, Hash, Heart, MapPin, MoreHorizontal, Plane, Users, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getMemberCenterNavSections } from '../../utils/memberNavigation';

const DISCOVERY_LINKS = [
  { id: 'all', name: '揪活動', path: '/events', icon: Hash, color: '#4b5563' },
  { id: 'sports', name: '揪運動', path: '/sports', icon: Activity, color: '#10b981' },
  { id: 'travel', name: '揪旅伴', path: '/travel', icon: Plane, color: '#6a9fd3' },
  { id: 'food', name: '美食', value: '美食', icon: Coffee, color: '#f59e0b' },
  { id: 'arts', name: '文藝', value: '文藝', icon: Heart, color: '#ec4899' },
  { id: 'party', name: '聚會', value: '聚會', icon: Users, color: '#8b5cf6' },
  { id: 'outdoor', name: '戶外', value: '戶外', icon: MapPin, color: '#14b8a6' },
  { id: 'learning', name: '學習', value: '學習', icon: BookOpen, color: '#6366f1' },
  { id: 'others', name: '其他', value: '其他', icon: MoreHorizontal, color: '#9ca3af' }
];

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

export default function SidebarDrawer({
  isOpen,
  onClose,
  mode = 'navigation',
  contextualDrawer = null,
  isAdmin = false
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const drawerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const titleRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const isMemberMode = mode === 'member';
  const isContextualMode = mode === 'contextual' && contextualDrawer;
  const title = isContextualMode ? contextualDrawer.title : (isMemberMode ? '我的' : '探索');
  const titleId = 'mobile-context-drawer-title';

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.requestAnimationFrame(() => (isContextualMode ? titleRef.current : closeButtonRef.current)?.focus());

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !drawerRef.current) return;
      const focusable = [...drawerRef.current.querySelectorAll(focusableSelector)];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isContextualMode, isOpen, onClose]);

  const navigateMember = (path) => {
    navigate(path);
    onClose();
  };

  const renderMemberNavigation = () => (
    <nav aria-label="手機會員導覽" style={{ display: 'grid', gap: '8px', padding: '4px 14px 24px' }}>
      {getMemberCenterNavSections(isAdmin).map(section => (
        <div key={section.label} style={{ display: 'grid', gap: '3px' }}>
          <div style={{ padding: '8px 12px 4px', color: '#94a3b8', fontSize: '10px', fontWeight: '800', letterSpacing: '0.08em' }}>{section.label}</div>
          {section.items.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
            return (
              <button
                key={item.key}
                type="button"
                aria-current={isActive ? 'page' : undefined}
                onClick={() => navigateMember(item.path)}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', minHeight: '48px', padding: '10px 13px', color: isActive ? '#376ba1' : '#475569', background: isActive ? '#eef4f9' : 'transparent', border: 'none', borderRadius: '13px', fontFamily: 'inherit', fontSize: '14px', fontWeight: isActive ? '800' : '700', textAlign: 'left', cursor: 'pointer' }}
              >
                <Icon size={19} aria-hidden="true" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );

  const renderDiscoveryNavigation = () => (
    <nav aria-label="探索導覽" style={{ display: 'grid', gap: '4px', padding: '4px 12px 24px' }}>
      {DISCOVERY_LINKS.map(item => {
        const Icon = item.icon;
        const target = item.path || `/events?category=${encodeURIComponent(item.value)}`;
        const isActive = item.path
          ? location.pathname === item.path
          : location.pathname === '/events' && new URLSearchParams(location.search).get('category') === item.value;
        return (
          <Link
            key={item.id}
            to={target}
            onClick={onClose}
            aria-current={isActive ? 'page' : undefined}
            style={{ display: 'flex', alignItems: 'center', gap: '13px', minHeight: '48px', padding: '9px 13px', color: isActive ? '#376ba1' : '#374151', background: isActive ? '#eef4f9' : 'transparent', borderRadius: '14px', fontSize: '15px', fontWeight: isActive ? '800' : '650', textDecoration: 'none' }}
          >
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', color: item.color, background: `${item.color}15`, borderRadius: '12px' }}>
              <Icon size={20} aria-hidden="true" />
            </span>
            {item.name}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.button
            type="button"
            aria-label="關閉側邊選單"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.2 }}
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', padding: 0, background: 'rgba(15, 23, 42, 0.52)', border: 0, zIndex: 2000, cursor: 'default' }}
          />
          <motion.aside
            ref={drawerRef}
            id="mobile-context-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={reduceMotion ? { duration: 0 } : { type: 'tween', duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
            style={{ position: 'fixed', top: 0, bottom: 0, left: 0, display: 'flex', flexDirection: 'column', width: 'min(88vw, 360px)', paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', background: '#ffffff', boxShadow: '8px 0 28px rgba(15,23,42,0.18)', zIndex: 2001 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minHeight: '68px', padding: '12px 16px', borderBottom: '1px solid #eef2f6' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 ref={titleRef} tabIndex={-1} id={titleId} style={{ margin: 0, color: '#1e293b', fontSize: '19px', fontWeight: '850', outline: 'none' }}>{title}</h2>
                {isContextualMode && contextualDrawer.description && <p style={{ margin: '3px 0 0', color: '#64748b', fontSize: '11px', lineHeight: 1.45 }}>{contextualDrawer.description}</p>}
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                aria-label="關閉側邊選單"
                onClick={onClose}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: '44px', height: '44px', color: '#475569', background: '#f1f5f9', border: 'none', borderRadius: '50%', cursor: 'pointer' }}
              >
                <X size={21} aria-hidden="true" />
              </button>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
              {isContextualMode
                ? <div style={{ padding: '16px' }}>{contextualDrawer.content}</div>
                : (isMemberMode ? renderMemberNavigation() : renderDiscoveryNavigation())}
            </div>

            {isContextualMode && (
              <div style={{ padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))', background: 'rgba(255,255,255,0.97)', borderTop: '1px solid #e2e8f0' }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{ width: '100%', minHeight: '48px', padding: '11px 16px', color: '#ffffff', background: contextualDrawer.accent || '#4e8bca', border: 'none', borderRadius: '14px', boxShadow: `0 7px 18px ${contextualDrawer.accent || '#4e8bca'}33`, fontFamily: 'inherit', fontSize: '15px', fontWeight: '800', cursor: 'pointer' }}
                >
                  查看結果{Number.isFinite(contextualDrawer.resultCount) ? `（${contextualDrawer.resultCount}）` : ''}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
